import React from 'react'
import { generatePath, useHistory } from 'react-router-dom'
import AddShipmentIcon from 'transfix-icons/build/AddShipmentIcon'
import BellIcon from 'transfix-icons/build/BellIcon'
import PaymentsIcon from 'transfix-icons/build/PaymentsIcon'
import SearchIcon from 'transfix-icons/build/SearchIcon'
import ShipmentsIcon from 'transfix-icons/build/ShipmentsIcon'
import { Button } from 'transfix-ui/components/Button'
import { Box, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import { useFeatureFlagContext } from 'utils/context/FeatureFlagsContext'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { MultiTruck } from 'components/atoms'

const useDashboardButtonsStyles = makeStyles(theme => ({
  container: {
    [theme.breakpoints.down('lg')]: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      gap: 20,
      width: '100%',
    },
  },
  button: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    textAlign: 'left',

    [theme.breakpoints.down('lg')]: {
      display: 'flex',
      flexDirection: 'column',
      background: brandColors.white,
      color: brandColors.black,
      height: 140,
      border: `1px solid ${brandColors.coolGray4}`,
      alignItems: 'center',
      textAlign: 'center',

      '& .MuiButton-label': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-around',
        height: 'inherit',
        alignItems: 'center',

        '& .MuiButton-startIcon': {
          background: brandColors.skyBlue2,
          borderRadius: '50%',
          height: 70,
          width: 70,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          margin: 0,

          '& svg': {
            width: 25,
            height: 25,
          },
        },
      },
    },
  },
  invertSvgColor: {
    filter: 'invert(1)',

    [theme.breakpoints.down('lg')]: {
      filter: 'none',
    },
  },
}))

export const DashboardButtons = () => {
  const { trackEvent } = useFMSAnalytics()
  const history = useHistory()
  const classes = useDashboardButtonsStyles()
  const { isMobile } = useBreakpointContext()
  const { getFlag } = useFeatureFlagContext()
  const { enabled: isHomePageUpdatesEnabled } = getFlag('homePageUpdates')

  const clickFindLoadHandler = () => {
    trackEvent('Find a load to book', 'CLICKED_POST_FIND_LOAD_CTA_IN_HOME_PAGE')
    history.push('/book')
  }

  const clickAddLoadHandler = () => {
    history.push('/loads/create-load')
  }

  const clickCheckPaymentHandler = () => {
    const url = generatePath(`${__ROOT__}/payments`)
    window.open(url)
  }

  const clickPostTruckHandler = () => {
    trackEvent('Truck Availability', 'CLICKED_POST_TRUCK_CTA_IN_HOME_PAGE')
    history.push('/truck-availability')
  }

  const clickManageMyLoadHandler = () => {
    trackEvent('Manage my loads', 'CLICKED_POST_MANAGE_LOADS_CTA_IN_HOME_PAGE')
    history.push('/loads')
  }

  const clickAlertLaneHandler = () => {
    trackEvent('Create a lane alert', 'CLICKED_POST_CREATE_LANE_ALERT_CTA_IN_HOME_PAGE')
    history.push('/transfix-lanes/add-alert')
  }

  return (
    <div className={isHomePageUpdatesEnabled ? classes.container : ''}>
      <Box pb={1}>
        <Button
          startIcon={<SearchIcon size='large' />}
          className={isHomePageUpdatesEnabled ? classes.button : ''}
          onClick={clickFindLoadHandler}
          data-test='find-load'
          color='primary'
          fullWidth={!isHomePageUpdatesEnabled ? true : !isMobile}>
          <span className='MuiButton-label'>Find a load to book</span>
        </Button>
      </Box>
      {isHomePageUpdatesEnabled ? (
        <>
          <Box pb={1}>
            <Button
              startIcon={
                <BellIcon className={classes.invertSvgColor} size='large' color='coolGray8' />
              }
              className={classes.button}
              onClick={clickAlertLaneHandler}
              data-test='create-lane-alert'
              color='primary'
              fullWidth={!isMobile}>
              Add Saved Lane
            </Button>
          </Box>
          <Box pb={1}>
            <Button
              startIcon={<ShipmentsIcon size='large' />}
              className={classes.button}
              onClick={clickManageMyLoadHandler}
              data-test='manage-my-loads'
              color='primary'
              fullWidth={!isMobile}>
              Manage my loads
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box pb={1}>
            <Button
              startIcon={<MultiTruck className='icon large' />}
              onClick={clickPostTruckHandler}
              data-test='post-truck-availability'
              color='primary'
              fullWidth>
              Post truck availability
            </Button>
          </Box>
          <Box pb={1}>
            <Button
              startIcon={<AddShipmentIcon size='large' />}
              onClick={clickAddLoadHandler}
              data-test='add-load'
              color='primary'
              fullWidth>
              Add a load to your schedule
            </Button>
          </Box>
          <Box pb={1}>
            <Button
              startIcon={<PaymentsIcon size='large' />}
              onClick={clickCheckPaymentHandler}
              data-test='check-payment'
              color='primary'
              fullWidth>
              Check your payment status
            </Button>
          </Box>
        </>
      )}
    </div>
  )
}
